'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/useToast';
import { useCancelSubscription } from '@/lib/query/useCancelSubscription';
import type { Subscription } from '@/lib/query/useSubscription';

interface CancelSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: Subscription | null | undefined;
  /** True if the user is on a trial; changes copy from "Cancel Subscription" to "Cancel Trial". */
  trialEndedAt?: string | null;
}

export function CancelSubscriptionDialog({
  isOpen,
  onClose,
  subscription,
  trialEndedAt,
}: CancelSubscriptionDialogProps) {
  const cancelSubscription = useCancelSubscription();
  const isTrial = subscription?.status === 'trialing';

  // For trial cancellation, the meaningful "access until" date is the trial
  // end (the first-charge date that's now being prevented). For an active
  // subscription, it's the current period end.
  const accessIso = isTrial
    ? (trialEndedAt ?? subscription?.currentPeriodEndedAt)
    : subscription?.currentPeriodEndedAt;
  const accessDate = accessIso
    ? new Date(accessIso).toLocaleDateString()
    : null;

  const title = isTrial ? 'Cancel Trial' : 'Cancel Subscription';
  const heading = isTrial
    ? 'Are you sure you want to cancel your trial?'
    : 'Are you sure you want to cancel your subscription?';
  const body = isTrial
    ? accessDate
      ? `Your trial will end on ${accessDate} and your card will not be charged. You can resubscribe at any time before then.`
      : 'Your trial will end and your card will not be charged.'
    : accessDate
      ? `Your subscription will remain active until ${accessDate}, then access will end.`
      : 'Your subscription will end at the end of the current billing period.';
  const confirmLabel = isTrial ? 'Cancel Trial' : 'Cancel Subscription';

  const handleConfirm = async () => {
    try {
      await cancelSubscription.mutateAsync();
      toast.success(
        isTrial ? 'Trial canceled' : 'Subscription canceled',
        accessDate
          ? `Access remains until ${accessDate}.`
          : 'Access will end at the end of the current period.'
      );
      onClose();
    } catch (error) {
      toast.error(
        'Cancellation failed',
        error instanceof Error ? error.message : 'Please try again.'
      );
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <div className="bg-destructive/10 flex items-start gap-3 rounded-md p-4">
          <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">{heading}</p>
            <p className="text-muted-foreground text-sm">{body}</p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={cancelSubscription.isPending}
          >
            Keep {isTrial ? 'Trial' : 'Subscription'}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={cancelSubscription.isPending}
          >
            {cancelSubscription.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Canceling...
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
